import { scaleSchema } from "@fatbees/schemas";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

const REFETCH_INTERVAL = 1000 * 60 * 15; // 15 minutes
const QUERY_KEY = ["scales"];

export function useScalesCollectionInstance(queryClient: QueryClient) {
  // We refetch to update the lastReport field, which will trigger ReadingsFetcher
  // to fetch the new readings .
  useEffect(() => {
    // As of TanStack DB v0.5 refetchInterval is unreliable, even with
    // refetchIntervalInBackground set to true.
    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [queryClient]);

  return useMemo(
    () =>
      createCollection(
        queryCollectionOptions({
          id: "scales",
          queryKey: QUERY_KEY,
          queryFn: async () => {
            const response = await fetch("/api/scales");
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const body = await response.json();

            return body.map((scale: unknown) => scaleSchema.parse(scale));
          },
          queryClient,
          getKey: (item) => item.id,
          schema: scaleSchema,
        }),
      ),
    [queryClient],
  );
}

export type ScalesCollection = ReturnType<typeof useScalesCollectionInstance>;
